/**
 * UserPreferencesService
 * Service for UserPreferences entity operations
 * Route: api/userpreferences
 */
import { CrudService } from '../base/CrudService';
/**
 * UserPreferencesService
 * Handles user preferences CRUD operations
 */
export class UserPreferencesService extends CrudService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? UserPreferencesService.DEFAULT_SERVICE_PATH,
            basePath: UserPreferencesService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== GET ALL ====================
    /**
     * Get all preferences with pagination
     * GET api/userpreferences?page=1&pageSize=50
     */
    async getAll(params) {
        const response = await this.getAllRaw(params);
        return response.data ?? [];
    }
    /**
     * Get all preferences with full response
     */
    getAllRaw(params) {
        const url = this.buildUrlWithParams(undefined, params);
        return this.http.get(url);
    }
    // ==================== GET BY ID ====================
    /**
     * Get preferences by ID
     * GET api/userpreferences/{id}
     */
    async getById(id) {
        const response = await this.getByIdRaw(id);
        if (!response.data) {
            throw new Error(`Preferences ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get preferences by ID with full response
     */
    getByIdRaw(id) {
        const url = this.buildUrl(String(id));
        return this.http.get(url);
    }
    // ==================== GET BY USER ID ====================
    /**
     * Get all preferences for a specific user
     * GET api/userpreferences/user/{userId}
     */
    async getByUserId(userId) {
        const response = await this.getByUserIdRaw(userId);
        return response.data ?? [];
    }
    /**
     * Get preferences by user ID with full response
     */
    getByUserIdRaw(userId) {
        const endpoint = this.getEndpoint('user', `user/${userId}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== GET BY USER AND APP ====================
    /**
     * Get preferences for a specific user and app
     * GET api/userpreferences/user/{userId}/app/{appId}
     */
    async getByUserAndApp(userId, appId) {
        const response = await this.getByUserAndAppRaw(userId, appId);
        if (!response.data) {
            throw new Error(`Preferences for user ${userId} and app ${appId} not found`);
        }
        return response.data;
    }
    /**
     * Get preferences by user and app with full response
     */
    getByUserAndAppRaw(userId, appId) {
        const endpoint = this.getEndpoint('user-app', `user/${userId}/app/${appId}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== CREATE PREFERENCES ====================
    /**
     * Create new user preferences
     * POST api/userpreferences
     */
    async create(data) {
        const response = await this.createRaw(data);
        if (!response.data) {
            throw new Error('Failed to create preferences');
        }
        return response.data;
    }
    /**
     * Create preferences with full response
     */
    createRaw(data) {
        const url = this.buildUrl();
        return this.http.post(url, data);
    }
    // ==================== UPDATE PREFERENCES ====================
    /**
     * Update user preferences
     * PUT api/userpreferences/{id}
     */
    async update(id, data) {
        const response = await this.updateRaw(id, data);
        if (!response.data) {
            throw new Error('Failed to update preferences');
        }
        return response.data;
    }
    /**
     * Update preferences with full response
     */
    updateRaw(id, data) {
        const url = this.buildUrl(String(id));
        return this.http.put(url, data);
    }
    // ==================== DELETE PREFERENCES ====================
    /**
     * Delete user preferences
     * DELETE api/userpreferences/{id}
     */
    async delete(id) {
        const response = await this.deleteRaw(id);
        return response.success;
    }
    /**
     * Delete preferences with full response
     */
    deleteRaw(id) {
        const url = this.buildUrl(String(id));
        return this.http.delete(url);
    }
    // ==================== MY PREFERENCES (Self-Service) ====================
    /**
     * Get all preferences for the authenticated user
     * GET api/userpreferences/my
     */
    async getMyPreferences() {
        const response = await this.getMyPreferencesRaw();
        return response.data ?? [];
    }
    /**
     * Get my preferences with full response
     */
    getMyPreferencesRaw() {
        const url = this.buildUrl('my');
        return this.http.get(url);
    }
    /**
     * Get preferences for the authenticated user for a specific app
     * GET api/userpreferences/my/app/{appId}
     */
    async getMyPreferencesByApp(appId) {
        const response = await this.getMyPreferencesByAppRaw(appId);
        if (!response.data) {
            throw new Error(`Preferences for app ${appId} not found`);
        }
        return response.data;
    }
    /**
     * Get my preferences by app with full response
     */
    getMyPreferencesByAppRaw(appId) {
        const url = this.buildUrl(`my/app/${appId}`);
        return this.http.get(url);
    }
    /**
     * Create preferences for the authenticated user
     * POST api/userpreferences/my
     */
    async createMyPreferences(data) {
        const response = await this.createMyPreferencesRaw(data);
        if (!response.data) {
            throw new Error('Failed to create preferences');
        }
        return response.data;
    }
    /**
     * Create my preferences with full response
     */
    createMyPreferencesRaw(data) {
        const url = this.buildUrl('my');
        return this.http.post(url, data);
    }
    /**
     * Update preferences for the authenticated user
     * PUT api/userpreferences/my/{id}
     */
    async updateMyPreferences(id, data) {
        const response = await this.updateMyPreferencesRaw(id, data);
        if (!response.data) {
            throw new Error('Failed to update preferences');
        }
        return response.data;
    }
    /**
     * Update my preferences with full response
     */
    updateMyPreferencesRaw(id, data) {
        const url = this.buildUrl(`my/${id}`);
        return this.http.put(url, data);
    }
    /**
     * Delete preferences for the authenticated user
     * DELETE api/userpreferences/my/{id}
     */
    async deleteMyPreferences(id) {
        const response = await this.deleteMyPreferencesRaw(id);
        return response.success;
    }
    /**
     * Delete my preferences with full response
     */
    deleteMyPreferencesRaw(id) {
        const url = this.buildUrl(`my/${id}`);
        return this.http.delete(url);
    }
}
UserPreferencesService.DEFAULT_SERVICE_PATH = 'users';
UserPreferencesService.BASE_PATH = 'api/userpreferences';
//# sourceMappingURL=UserPreferencesService.js.map